import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Eye, Trash2, Target, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Business Card Printing",
  "Digital Vinyl Printing",
  "Bill Book Printing",
  "Digital Flex Printing",
  "Brochure Offset Printing",
  "Offset Printing Stickers",
  "Other",
];

const statusColors = {
  Open: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  Contacted: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
  "Quotation Sent": { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  Approved: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

export default function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", requirements: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: getAuthHeaders(), withCredentials: true });
      setLeads(data);
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      setForm({ name: "", email: "", phone: "", service: "", requirements: "", remarks: "" });
      setShowForm(false);
      fetchLeads();
    } catch (err) {
      console.error("Failed to create lead", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      fetchLeads();
    } catch (err) {
      console.error("Failed to delete lead", err);
    }
  };

  const filtered = leads.filter((l) => {
    const matchSearch = !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "All" || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const counts = {
    All: leads.length,
    Open: leads.filter(l => l.status === "Open").length,
    Contacted: leads.filter(l => l.status === "Contacted").length,
    "Quotation Sent": leads.filter(l => l.status === "Quotation Sent").length,
    Approved: leads.filter(l => l.status === "Approved").length,
  };

  return (
    <div data-testid="page-leads" className="min-h-screen bg-[#F4F3EF]">
      {/* Header */}
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Leads</h1>
        <button
          onClick={() => setShowForm(true)}
          data-testid="add-lead-btn"
          className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200"
        >
          <Plus size={16} /> Add Lead
        </button>
      </div>

      <div className="px-5 sm:px-8 py-6 space-y-6">
        {/* Status tabs */}
        <div className="flex flex-wrap gap-2">
          {["All", "Open", "Contacted", "Quotation Sent", "Approved"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 text-xs font-bold border transition-colors ${
                filterStatus === s
                  ? "bg-[#161616] text-white border-[#161616]"
                  : "bg-white text-[#5A5955] border-[#D9D7D0] hover:border-[#161616]"
              }`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5955]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            data-testid="leads-search"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#D9D7D0] text-sm focus:border-[#161616] outline-none transition-colors"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <Target size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No leads found. Click "Add Lead" to create one.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#D9D7D0] overflow-x-auto">
            <table className="w-full text-left" data-testid="leads-table">
              <thead>
                <tr className="border-b border-[#D9D7D0]">
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Name</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Email</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Phone</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Service</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Assigned To</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead) => {
                  const sc = statusColors[lead.status] || statusColors.Open;
                  return (
                    <tr key={lead.id} className="border-b border-[#D9D7D0] hover:bg-[#F7F6F2] transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-[#161616]">{lead.name}</td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{lead.email}</td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{lead.phone || "—"}</td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{lead.service || "—"}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-bold ${sc.bg} ${sc.text} border ${sc.border}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#5A5955]">{lead.assigned_to_name || "—"}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/leads/${lead.id}`)}
                            data-testid={`view-lead-${lead.id}`}
                            className="p-2 hover:bg-[#F4F3EF] transition-colors"
                            title="View"
                          >
                            <Eye size={16} className="text-[#5A5955]" />
                          </button>
                          <button
                            onClick={() => handleDelete(lead.id)}
                            data-testid={`delete-lead-${lead.id}`}
                            className="p-2 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-lead-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">Add New Lead</h2>
              <button onClick={() => setShowForm(false)} data-testid="close-modal-btn" className="p-1 hover:bg-[#F4F3EF]">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="lead-input-name" className="field" placeholder="Full name" />
              </div>
              <div>
                <label className="field-label">Email *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="lead-input-email" className="field" placeholder="email@example.com" />
              </div>
              <div>
                <label className="field-label">Phone Number</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="lead-input-phone" className="field" placeholder="+91 XXXXX XXXXX" />
              </div>
              <div>
                <label className="field-label">Select Service</label>
                <select value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} data-testid="lead-input-service" className="field">
                  <option value="">Select a service</option>
                  {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Requirements</label>
                <textarea rows={3} value={form.requirements} onChange={(e) => setForm({...form, requirements: e.target.value})} data-testid="lead-input-requirements" className="field resize-y" placeholder="Describe the requirements..." />
              </div>
              <div>
                <label className="field-label">Remarks</label>
                <textarea rows={2} value={form.remarks} onChange={(e) => setForm({...form, remarks: e.target.value})} data-testid="lead-input-remarks" className="field resize-y" placeholder="Any additional notes..." />
              </div>
              <button type="submit" disabled={submitting} data-testid="submit-lead-btn" className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200 disabled:opacity-50">
                {submitting ? "Creating..." : "Create Lead"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
